import { useEffect, useState, useRef } from "react";
import { Button, Typography, Card, Box, TextField, Grid } from "@mui/material";
import PageHeader from "src/@core/components/page-header";
import Translations from "src/layouts/components/Translations";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { DefaultPaginationSettings } from "src/constants/general.const";
import { toastError, toastSuccess } from "src/utils/utils";
import DialogConfirmation from "src/views/dialog/DialogConfirmation";
import TableRegions from "src/views/tables/TableRegions";
import DialogUserStatusUpdate from "src/views/dialog/DialogUserStatusUpdate";
import DialogAddRegion from "src/views/dialog/DialogAddRegion";

const RegionPage = () => {
    const searchTimeoutRef = useRef();
    const [loading, setLoading] = useState(false);
    const [regions, setRegions] = useState([]);
    const [search, setSearch] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(
        DefaultPaginationSettings.ROWS_PER_PAGE
    );
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [regionDialogOpen, setRegionDialogOpen] = useState(false);
    const [regionToEdit, setRegionToEdit] = useState(null);
    const [regionToDelete, setRegionToDelete] = useState(null);
    const [dialogMode, setDialogMode] = useState("add");
    const [confirmOpen, setConfirmOpen] = useState(false);

    const toggleConfirm = (e, region = null) => {
        setConfirmOpen((prev) => !prev);
        setRegionToDelete(region);
    };

    // Function to open status dialog
    const handleStatusDialogOpen = (region) => {
        setSelectedRegion(region);
        setStatusDialogOpen(true);
    };

    // Function to close status dialog
    const handleStatusDialogClose = () => {
        setSelectedRegion(null);
        setStatusDialogOpen(false);
    };

    // Function to open region dialog for add
    const handleAddRegion = () => {
        setRegionToEdit(null);
        setDialogMode("add");
        setRegionDialogOpen(true);
    };

    // Function to open region dialog for edit
    const handleEditRegion = (e, region) => {
        console.log("region", region);
        setRegionToEdit(region);
        setDialogMode("edit");
        setRegionDialogOpen(true);
    };

    // Function to close region dialog
    const handleRegionDialogClose = () => {
        setRegionDialogOpen(false);
        setRegionToEdit(null);
        setDialogMode("add");
    };

    const fetchData = ({ currentPage, pageSize, search }) => {
        setLoading(true);
        const params = { page: currentPage, limit: pageSize, search };

        axiosInstance
            .get(ApiEndPoints.REGIONS?.list, { params })
            .then((response) => {
                setRegions(response.data?.data?.regions || []);
                setTotalCount(response.data?.data?.totalRecords || 0);
            })
            .catch((error) => toastError(error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData({ currentPage, pageSize, search });
    }, [currentPage, pageSize, search]);

    const handleSearchChange = (e) => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        searchTimeoutRef.current = setTimeout(() => {
            setSearch(e.target.value);
            setCurrentPage(1);
        }, 500);
    };

    const onConfirmDelete = () => {
        axiosInstance
            .delete(ApiEndPoints.REGIONS.delete(regionToDelete?.id))
            .then((response) => {
                toastSuccess(response.data?.message || "Region deleted successfully");
                fetchData({ currentPage, pageSize, search });
                setConfirmOpen(false);
                setRegionToDelete(null);
            })
            .catch((error) => {
                toastError(error);
            });
    };

    return (
        <>
            <Grid container spacing={4} padding={2}>
                <Grid item xs={12}>
                    <Box sx={{
                        p: 5,
                        mb: 0,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        <PageHeader
                            title={
                                <Typography variant="h5">
                                    <Translations text="Region" />
                                </Typography>
                            }
                            action={
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleAddRegion}
                                >
                                    Add Region
                                </Button>
                            }
                        />
                    </Box>
                    <Card>
                        <Box
                            sx={{
                                p: 5,
                                pb: 0, display: "flex", alignItems: "center",justifyContent:"flex-end"
                            }} >
                            <TextField
                                type="search"
                                size="small"
                                placeholder="Search"
                                onChange={handleSearchChange}
                            />
                        </Box>
                        <Box sx={{ p: 5 }}>
                            <TableRegions
                                rows={regions}
                                loading={loading}
                                totalCount={totalCount}
                                setCurrentPage={setCurrentPage}
                                currentPage={currentPage}
                                setPageSize={setPageSize}
                                pageSize={pageSize}
                                toggleEdit={handleEditRegion}
                                toggleStatus={handleStatusDialogOpen}
                                toggleDelete={toggleConfirm}
                            />
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            <DialogAddRegion
                open={regionDialogOpen}
                onClose={handleRegionDialogClose}
                onSuccess={() => {
                    fetchData({
                        currentPage: currentPage,
                        pageSize: pageSize,
                        search: search,
                    });
                }}
                mode={dialogMode}
                dataToEdit={regionToEdit}
            />

            <DialogUserStatusUpdate
                open={statusDialogOpen}
                toggle={handleStatusDialogClose}
                dataToEdit={selectedRegion}
                endpointType="REGIONS"
                onSuccess={() => {
                    fetchData({
                        currentPage: currentPage,
                        pageSize: pageSize,
                        search: search,
                    });
                }}
            />

            <DialogConfirmation
                open={confirmOpen}
                toggle={toggleConfirm}
                title="Delete Region"
                subtitle="Are you sure you want to delete this region?"
                onConfirm={onConfirmDelete}
            />
        </>
    );
};

export default RegionPage;
